import type { DecisionQuestion } from 'jev-web';
import type { ScanResult, SignalValue } from '../engine/types';

/**
 * Serialize a scan into the natural-language "state" text that the typed-
 * decision model consumes.
 *
 * IMPORTANT — read this before trusting the model's output:
 * jev-web runs a text classifier (DeBERTa-v3-large) whose training domain is
 * natural-language text. Fingerprint signals are structured, largely
 * categorical data, so this rendering is deliberately readable prose: it puts
 * the observations into the form the model was actually trained to read, which
 * gives it a fighting chance rather than feeding it `Win32, 12, true, false`.
 *
 * It is still out-of-distribution relative to the model's own training data, so
 * the result is presented as an experimental opinion and never feeds the
 * deterministic bot score. See the AI panel's disclaimer.
 */

// Laya's context is 1024 tokens (~4000 chars at ~4 chars/token for this kind
// of text); open-jev's fallback is 256. We aim at the larger budget and let
// `truncated` in the result tell the truth about which family ran.
const MAX_SIGNAL_CHARS = 4000;
const TRUNCATION_NOTE = '… further signals omitted for length.';
/** The note is appended after the loop, so reserve its room up front. */
const TRUNCATION_RESERVE = TRUNCATION_NOTE.length + 1;

/** One line per interesting signal, with a short human gloss. */
function describe(probeId: string, name: string, v: SignalValue): string | null {
  if (v.status !== 'ok' || v.value === null) return null;
  const val = v.value;

  // Booleans read as statements.
  if (typeof val === 'boolean') {
    return `- ${name}: ${val ? 'yes' : 'no'}`;
  }
  if (typeof val === 'number') {
    return `- ${name}: ${val}`;
  }
  if (typeof val === 'string') {
    if (!val) return null;
    const short = val.length > 180 ? `${val.slice(0, 180)}…` : val;
    return `- ${name}: ${short}`;
  }
  if (Array.isArray(val)) {
    if (!val.length) return null;
    const head = val.slice(0, 8).map((x) => (typeof x === 'object' ? '[obj]' : String(x)));
    return `- ${name}: ${head.join(', ')}${val.length > 8 ? ` (+${val.length - 8} more)` : ''}`;
  }
  if (typeof val === 'object') {
    // Flatten one level; deeper structures are noise for a text model.
    const entries = Object.entries(val as Record<string, unknown>)
      .filter(([, x]) => x !== null && x !== undefined)
      .slice(0, 8)
      .map(([k, x]) => {
        if (typeof x === 'object' && x !== null) {
          if (Array.isArray(x)) return `${k}=[${x.length} items]`;
          const flags = Object.entries(x as Record<string, unknown>)
            .filter(([, y]) => y === true)
            .map(([kk]) => kk);
          return flags.length ? `${k}={${flags.slice(0, 5).join('|')}}` : `${k}={}`;
        }
        return `${k}=${String(x)}`;
      });
    if (!entries.length) return null;
    return `- ${name}: ${entries.join(', ')}`;
  }
  void probeId;
  return null;
}

/** Build the natural-language state the model reads. */
export function buildState(result: ScanResult): string {
  const lines: string[] = [];
  // The budget covers the WHOLE document. `spent` tracks what is already in
  // `lines`; `push` is the only way to add to it, so the two can never drift.
  let spent = 0;
  const remaining = (): number => MAX_SIGNAL_CHARS - spent;
  const push = (line: string): boolean => {
    const cost = line.length + 1; // +1 for the newline join
    if (cost > remaining()) return false;
    spent += cost;
    lines.push(line);
    return true;
  };

  // Frame the task in the model's domain: a support/analysis report.
  push('A browser fingerprinting scan produced the following observations.');
  push(
    `Automated-browser suspicion score from a deterministic rule engine: ${result.botScore} out of 100.`,
  );
  push('');

  const findingLines = result.findings
    .slice(0, 12)
    .map((f) => `- finding (${f.severity}): ${f.title}`);
  if (findingLines.length) {
    push('Detection findings:');
    for (const l of findingLines) {
      if (!push(l)) break;
    }
    push('');
  }

  push('Measured browser and device signals:');
  // Rank probes so the most detection-relevant evidence survives truncation.
  // Object order is arbitrary, and on the 256-token open-jev fallback most of
  // the text gets cut, so what we keep first is the difference between a useful
  // verdict and a coin flip.
  const ranked = Object.entries(result.signals).sort(
    (a, b) => probePriority(b[0]) - probePriority(a[0]),
  );

  let truncated = false;
  outer: for (const [probeId, signals] of ranked) {
    // Leave room for the truncation note so the document never overshoots.
    if (remaining() <= TRUNCATION_RESERVE) {
      truncated = true;
      break;
    }
    if (!push(`[${probeId}]`)) {
      truncated = true;
      break;
    }
    for (const [name, v] of Object.entries(signals)) {
      const d = describe(probeId, name, v);
      if (!d) continue;
      if (!push(d)) {
        truncated = true;
        break outer;
      }
    }
  }
  if (truncated) lines.push(TRUNCATION_NOTE);

  return lines.join('\n');
}

/**
 * Higher = more worth keeping when the state text is truncated. The scores are
 * about evidence value, not importance to the UI: automation artifacts and
 * hardware/coherence contradictions first, verbose capability dumps last.
 */
const PROBE_PRIORITY: Record<string, number> = {
  automation: 100,
  coherence: 95,
  crossContext: 95,
  general: 90,
  webgl: 85,
  webglRender: 85,
  canvas: 80,
  clientHints: 75,
  fonts: 70,
  codecs: 70,
  mediaCapabilities: 65,
  sab: 60,
  webAssembly: 55,
  wasmSimd: 55,
  display: 50,
  locale: 50,
  engineTiming: 45,
  plugins: 45,
  mathFingerprint: 40,
  compressor: 35,
  ai: 35,
  framing: 35,
  network: 30,
  edge: 30,
  cache: 25,
  webrtc: 25,
  webgpu: 20,
  performance: 15,
  media: 15,
  mediaQueries: 15,
  fontEnumerate: 10,
  websocket: 5,
  audio: 5,
  motion: 5,
  keyboard: 5,
};

function probePriority(id: string): number {
  return PROBE_PRIORITY[id] ?? 1;
}

export { type DecisionQuestion };

/**
 * The typed questions asked of the model. These map the decision onto the
 * three question types jev-web supports, and the options are phrased as the
 * natural categories a support-triage model would output.
 */
export const QUESTIONS: DecisionQuestion[] = [
  {
    type: 'choice',
    instructions:
      'How likely is it that this browser session is automated (a bot, headless browser, or automated tool) rather than a real person?',
    options: ['certainly a real person', 'probably a real person', 'uncertain', 'probably automated', 'certainly automated'],
  },
  {
    type: 'score',
    instructions:
      'How suspicious is the evidence overall, from not at all suspicious to extremely suspicious?',
    options: ['not suspicious', 'slightly suspicious', 'moderately suspicious', 'very suspicious', 'extremely suspicious'],
  },
  {
    type: 'noul',
    instructions: 'Does the fingerprint show a contradiction, such as a User-Agent that disagrees with the graphics or hardware stack?',
  },
  {
    type: 'noul',
    instructions: 'Does this look like a genuine unmodified consumer browser rather than an automated or anti-detect environment?',
  },
  {
    type: 'noul',
    instructions: 'Should a website operator treat this session as high risk and challenge it?',
  },
];
